/**
 * Shared filter state and derived values for the prompt library (fullscreen and modal).
 * Avoids duplicating the same state and filtering logic in both places.
 *
 * - Category filters (My Prompts, Favorites): Mutually exclusive - only one can be selected at a time
 * - Other filters (Use Cases, Roles, Connectors): Applied independently to each category
 */
import { useCallback, useMemo, useState } from 'react';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { PromptCard } from '@atlassian/rovo-conversation-actions/prompt-cards';

import { filterPromptCards } from '../utils/filter-prompt-cards';

export type PromptLibraryCategory = 'all' | 'myPrompts' | 'favorites';

export type UsePromptLibraryFiltersResult = {
	searchQuery: string;
	selectedCategory: PromptLibraryCategory;
	selectedCategoryFilters: Set<Exclude<PromptLibraryCategory, 'all'>>;
	selectedUseCases: string[];
	selectedRoles: string[];
	selectedConnectors: string[];
	searchablePrompts: string[];
	filteredPromptCards: PromptCard[];
	filteredMyPromptCards: PromptCard[];
	filteredFavoritePromptCards: PromptCard[];
	promptCategories: Record<PromptLibraryCategory, PromptCard[]>;
	cardsToShow: PromptCard[];
	resultCount: number;
	hasActiveFilters: boolean;
	handleSearchChange: (query: string, _filtered: string[]) => void;
	handleMyPromptsClick: () => void;
	handleFavoritesClick: () => void;
	handleUseCaseFilterChange: (filters: string[]) => void;
	handleRoleFilterChange: (filters: string[]) => void;
	handleConnectorFilterChange: (filters: string[]) => void;
	handleClearFilters: () => void;
};

export function usePromptLibraryFilters(
	promptCards: PromptCard[],
	myPromptCards: PromptCard[],
): UsePromptLibraryFiltersResult {
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedUseCases, setSelectedUseCases] = useState<string[]>([]);
	const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
	const [selectedConnectors, setSelectedConnectors] = useState<string[]>([]);
	const [selectedCategoryFilters, setSelectedCategoryFilters] = useState<
		Set<Exclude<PromptLibraryCategory, 'all'>>
	>(new Set());

	// For backward compatibility, derive selectedCategory from selectedCategoryFilters
	const selectedCategory: PromptLibraryCategory = useMemo(() => {
		if (selectedCategoryFilters.size === 0) {
			return 'all';
		}
		if (selectedCategoryFilters.size === 1) {
			return selectedCategoryFilters.has('myPrompts') ? 'myPrompts' : 'favorites';
		}
		// If both are selected, we'll show 'all' but filter the results
		return 'all';
	}, [selectedCategoryFilters]);

	const searchablePrompts = useMemo(() => promptCards.map((card) => card.title), [promptCards]);

	const filteredPromptCards = useMemo(
		() =>
			filterPromptCards(
				promptCards,
				searchQuery,
				selectedUseCases,
				selectedRoles,
				selectedConnectors,
			),
		[promptCards, searchQuery, selectedUseCases, selectedRoles, selectedConnectors],
	);

	const filteredMyPromptCards = useMemo(
		() =>
			filterPromptCards(
				myPromptCards,
				searchQuery,
				selectedUseCases,
				selectedRoles,
				selectedConnectors,
			),
		[myPromptCards, searchQuery, selectedUseCases, selectedRoles, selectedConnectors],
	);

	const filteredFavoritePromptCards = useMemo(() => {
		const favoriteCards = promptCards.filter((card) => card.isFavorited === true);
		return filterPromptCards(
			favoriteCards,
			searchQuery,
			selectedUseCases,
			selectedRoles,
			selectedConnectors,
		);
	}, [promptCards, searchQuery, selectedUseCases, selectedRoles, selectedConnectors]);

	const promptCategories = useMemo(
		() =>
			({
				all: filteredPromptCards,
				myPrompts: filteredMyPromptCards,
				favorites: filteredFavoritePromptCards,
			}) as Record<PromptLibraryCategory, PromptCard[]>,
		[filteredPromptCards, filteredMyPromptCards, filteredFavoritePromptCards],
	);

	const cardsToShow = useMemo(
		() => promptCategories[selectedCategory],
		[selectedCategory, promptCategories],
	);

	const resultCount = cardsToShow.length;
	const hasActiveFilters =
		selectedUseCases.length > 0 ||
		selectedRoles.length > 0 ||
		selectedConnectors.length > 0 ||
		selectedCategoryFilters.size > 0;

	const { sendAnalyticsEvent } = useAnalytics();

	const handleSearchChange = useCallback((query: string, _filtered: string[]) => {
		setSearchQuery(query);
	}, []);

	const handleMyPromptsClick = useCallback(() => {
		setSelectedCategoryFilters((prev) => {
			if (prev.has('myPrompts')) {
				return new Set();
			}
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'promptLibraryCategoryTab',
				attributes: {
					category: 'myPrompts',
				},
			});
			return new Set(['myPrompts']);
		});
	}, [sendAnalyticsEvent]);

	const handleFavoritesClick = useCallback(() => {
		setSelectedCategoryFilters((prev) => {
			if (prev.has('favorites')) {
				return new Set();
			}
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'promptLibraryCategoryTab',
				attributes: {
					category: 'favorites',
				},
			});
			return new Set(['favorites']);
		});
	}, [sendAnalyticsEvent]);

	const handleUseCaseFilterChange = useCallback((filters: string[]) => {
		setSelectedUseCases(filters);
	}, []);

	const handleRoleFilterChange = useCallback((filters: string[]) => {
		setSelectedRoles(filters);
	}, []);

	const handleConnectorFilterChange = useCallback((filters: string[]) => {
		setSelectedConnectors(filters);
	}, []);

	const handleClearFilters = useCallback(() => {
		setSelectedUseCases([]);
		setSelectedRoles([]);
		setSelectedConnectors([]);
		setSelectedCategoryFilters(new Set());
	}, []);

	return {
		searchQuery,
		selectedCategory,
		selectedCategoryFilters,
		selectedUseCases,
		selectedRoles,
		selectedConnectors,
		searchablePrompts,
		filteredPromptCards,
		filteredMyPromptCards,
		filteredFavoritePromptCards,
		promptCategories,
		cardsToShow,
		resultCount,
		hasActiveFilters,
		handleSearchChange,
		handleMyPromptsClick,
		handleFavoritesClick,
		handleUseCaseFilterChange,
		handleRoleFilterChange,
		handleConnectorFilterChange,
		handleClearFilters,
	};
}
